import { Logger } from "core/Misc/logger";
const InstalledExtensionsKey = "Extensions/InstalledExtensions";
const ExtensionInstalledKeyPrefix = "Extensions/IsExtensionInstalled";
function GetExtensionInstalledKey(name) {
    return `${ExtensionInstalledKeyPrefix}/${name}`;
}
function GetExtensionIdentity(feed, name) {
    return `${feed}/${name}`;
}
/**
 * Manages the installation, uninstallation, enabling, and disabling of extensions.
 */
export class ExtensionManager {
    constructor(_namespace, _serviceContainer, _feeds, _onInstallFailed) {
        this._namespace = _namespace;
        this._serviceContainer = _serviceContainer;
        this._feeds = _feeds;
        this._onInstallFailed = _onInstallFailed;
        this._installedExtensions = new Map();
        this._stateChangedHandlers = new Map();
    }
    /**
     * Creates a new instance of the ExtensionManager.
     * This will automatically rehydrate previously installed and enabled extensions.
     * @param namespace The namespace to use for storing extension state in local storage.
     * @param serviceContainer The service container to use.
     * @param feeds The extension feeds to include.
     * @param onInstallFailed A callback that is called when an extension installation fails.
     * @returns A promise that resolves to the new instance of the ExtensionManager.
     */
    static async CreateAsync(namespace, serviceContainer, feeds, onInstallFailed) {
        namespace = `Babylon/${namespace}`;
        const extensionManager = new ExtensionManager(namespace, serviceContainer, feeds, onInstallFailed);
        // Rehydrate installed extensions.
        const installedExtensionNames = JSON.parse(localStorage.getItem(`${namespace}/${InstalledExtensionsKey}`) ?? "[]");
        for (const installedExtensionName of installedExtensionNames) {
            const installedExtensionRaw = localStorage.getItem(`${namespace}/${GetExtensionInstalledKey(installedExtensionName)}`);
            if (installedExtensionRaw) {
                const installedExtensionData = JSON.parse(installedExtensionRaw);
                const feed = feeds.find((feed) => feed.name === installedExtensionData.feed);
                if (feed) {
                    const installedExtension = extensionManager._createInstalledExtension(installedExtensionData.metadata, feed);
                    extensionManager._installedExtensions.set(installedExtension.metadata.name, installedExtension);
                }
            }
        }
        // Load installed and enabled extensions.
        const enablePromises = [];
        for (const extension of extensionManager._installedExtensions.values()) {
            enablePromises.push((async () => {
                try {
                    await extensionManager._enableAsync(extension.metadata, false, false);
                }
                catch {
                    // If enabling the extension fails, uninstall it. The extension install fail callback will still be called,
                    // so the owner of the ExtensionManager instance can decide what to do with the error.
                    await extensionManager._uninstallAsync(extension.metadata, false);
                }
            })());
        }
        await Promise.all(enablePromises);
        return extensionManager;
    }
    /**
     * Gets the names of the feeds that are included in the extension manager.
     * @returns The names of the feeds.
     */
    get feedNames() {
        return this._feeds.map((feed) => feed.name);
    }
    /**
     * Queries the extension manager for extensions.
     * @param filter The filter to apply to the query.
     * @param feeds The feeds to include in the query.
     * @param installedOnly Whether to only include installed extensions.
     * @returns A promise that resolves to the extension query.
     */
    async queryExtensionsAsync(filter = "", feeds = this.feedNames, installedOnly = false) {
        if (installedOnly) {
            const installedExtensions = Array.from(this._installedExtensions.values()).filter((installedExtension) => feeds.includes(installedExtension.feed.name));
            return {
                totalCount: installedExtensions.length,
                getExtensionsAsync: async (index, count) => {
                    return installedExtensions.slice(index, index + count).map((installedExtension) => this._createExtension(installedExtension.metadata, installedExtension.feed));
                },
            };
        }
        const queries = await Promise.all(this._feeds.filter((feed) => feeds.includes(feed.name)).map(async (feed) => Object.assign(await feed.queryExtensionsAsync(filter), { feed })));
        const totalCount = queries.reduce((sum, query) => sum + query.totalCount, 0);
        return {
            totalCount,
            getExtensionsAsync: async (index, count) => {
                const extensions = new Array();
                let remaining = count;
                for (const query of queries) {
                    if (remaining <= 0) {
                        break;
                    }
                    if (index >= query.totalCount) {
                        index -= query.totalCount;
                        continue;
                    }
                    // This is intentionally sequential as we are querying for results until the count of results is met.
                    // eslint-disable-next-line no-await-in-loop
                    const metadataSlice = await query.getExtensionMetadataAsync(index, remaining);
                    extensions.push(...metadataSlice.map((metadata) => this._createExtension(metadata, query.feed)));
                    remaining -= metadataSlice.length;
                    index = 0;
                }
                return extensions;
            },
        };
    }
    /**
     * Disposes the extension manager.
     */
    dispose() {
        for (const installedExtension of this._installedExtensions.values()) {
            // eslint-disable-next-line github/no-then
            this._disableAsync(installedExtension.metadata, false).catch((error) => {
                Logger.Warn(`Failed to disable extension ${installedExtension.metadata.name}: ${error}`);
            });
        }
        this._stateChangedHandlers.clear();
    }
    _getInstalledExtensionStorageKey(metadata, feed) {
        return `${this._namespace}/${GetExtensionInstalledKey(GetExtensionIdentity(feed.name, metadata.name))}`;
    }
    _updateInstalledExtensionsStorage() {
        localStorage.setItem(`${this._namespace}/${InstalledExtensionsKey}`, JSON.stringify(Array.from(this._installedExtensions.values()).map((extension) => GetExtensionIdentity(extension.feed.name, extension.metadata.name))));
    }
    async _installAsync(metadata, feed, isNestedStateChange) {
        let installedExtension = this._installedExtensions.get(metadata.name);
        if (!installedExtension) {
            installedExtension = this._createInstalledExtension(metadata, feed);
            installedExtension.isStateChanging = true;
            this._installedExtensions.set(metadata.name, installedExtension);
            try {
                // Enable the extension.
                await this._enableAsync(metadata, true, true);
            }
            catch (error) {
                this._installedExtensions.delete(metadata.name);
                throw error;
            }
            finally {
                !isNestedStateChange && (installedExtension.isStateChanging = false);
            }
            // Mark the extension as being installed.
            localStorage.setItem(this._getInstalledExtensionStorageKey(metadata, feed), JSON.stringify({
                feed: feed.name,
                metadata,
            }));
            this._updateInstalledExtensionsStorage();
        }
        return installedExtension;
    }
    async _uninstallAsync(metadata, isNestedStateChange) {
        const installedExtension = this._installedExtensions.get(metadata.name);
        if (installedExtension && (isNestedStateChange || !installedExtension.isStateChanging)) {
            try {
                !isNestedStateChange && (installedExtension.isStateChanging = true);
                // Disable the extension.
                await this._disableAsync(metadata, true);
                // Remove the extension from in memory.
                this._installedExtensions.delete(metadata.name);
                // Mark the extension as being uninstalled.
                localStorage.removeItem(this._getInstalledExtensionStorageKey(metadata, installedExtension.feed));
                this._updateInstalledExtensionsStorage();
            }
            finally {
                !isNestedStateChange && (installedExtension.isStateChanging = false);
            }
        }
    }
    async _enableAsync(metadata, isInitialInstall, isNestedStateChange) {
        const installedExtension = this._installedExtensions.get(metadata.name);
        if (installedExtension && (isNestedStateChange || !installedExtension.isStateChanging)) {
            try {
                !isNestedStateChange && (installedExtension.isStateChanging = true);
                // If we haven't done so already, load the extension module.
                if (!installedExtension.extensionModule) {
                    installedExtension.extensionModule = await installedExtension.feed.getExtensionModuleAsync(metadata.name);
                }
                if (!installedExtension.extensionModule) {
                    throw new Error(`Unable to load extension module for "${metadata.name}" from feed "${installedExtension.feed.name}".`);
                }
                // Register the ServiceDefinitions.
                let servicesRegistrationToken = null;
                if (installedExtension.extensionModule.default.serviceDefinitions) {
                    servicesRegistrationToken = this._serviceContainer.addServices(...installedExtension.extensionModule.default.serviceDefinitions);
                }
                // Create a registration token to for dispose.
                installedExtension.registrationToken = {
                    dispose: () => {
                        servicesRegistrationToken?.dispose();
                    },
                };
            }
            catch (error) {
                this._onInstallFailed({
                    extension: metadata,
                    error,
                });
                throw error;
            }
            finally {
                !isNestedStateChange && (installedExtension.isStateChanging = false);
            }
        }
    }
    async _disableAsync(metadata, isNestedStateChange) {
        const installedExtension = this._installedExtensions.get(metadata.name);
        if (installedExtension && (isNestedStateChange || !installedExtension.isStateChanging)) {
            try {
                !isNestedStateChange && (installedExtension.isStateChanging = true);
                // Unregister the service registrations.
                installedExtension.registrationToken?.dispose();
            }
            finally {
                !isNestedStateChange && (installedExtension.isStateChanging = false);
            }
        }
    }
    _addStateChangedHandler(metadata, handler) {
        let stateChangedHandlers = this._stateChangedHandlers.get(metadata.name);
        if (!stateChangedHandlers) {
            this._stateChangedHandlers.set(metadata.name, (stateChangedHandlers = new Set()));
        }
        stateChangedHandlers.add(handler);
        return {
            dispose: () => {
                stateChangedHandlers.delete(handler);
                if (stateChangedHandlers.size === 0) {
                    this._stateChangedHandlers.delete(metadata.name);
                }
            },
        };
    }
    _createExtension(metadata, feed) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const extensionManager = this;
        return {
            metadata,
            get isStateChanging() {
                return extensionManager._installedExtensions.get(metadata.name)?.isStateChanging ?? false;
            },
            get isInstalled() {
                return extensionManager._installedExtensions.has(metadata.name);
            },
            installAsync: async () => {
                await extensionManager._installAsync(metadata, feed, false);
            },
            uninstallAsync: async () => await extensionManager._uninstallAsync(metadata, false),
            addStateChangedHandler: (handler) => extensionManager._addStateChangedHandler(metadata, handler),
        };
    }
    _createInstalledExtension(metadata, feed) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const extensionManager = this;
        let isStateChanging = false;
        return {
            metadata,
            feed,
            get isStateChanging() {
                return isStateChanging;
            },
            set isStateChanging(value) {
                if (value !== isStateChanging) {
                    isStateChanging = value;
                    extensionManager._stateChangedHandlers.get(this.metadata.name)?.forEach((handler) => handler());
                }
            },
        };
    }
}
//# sourceMappingURL=extensionManager.js.map